import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Share2 } from 'lucide-react';
import { useAuthSession } from '@/hooks/use-auth-session';
import { AddPlaceDialog } from '@/components/collections/AddPlaceDialog';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { PlaylistPlaceCard } from '@/components/collections/PlaylistPlaceCard';
import { SEO } from '@/components/SEO';
import { ShareCollectionDialog } from '@/components/collections/ShareCollectionDialog';

export default function Collection() {
  const { id, name } = useParams();
  const navigate = useNavigate();
  const session = useAuthSession();
  const [isAddPlaceDialogOpen, setIsAddPlaceDialogOpen] = React.useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = React.useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { data: playlist, isLoading: isPlaylistLoading } = useQuery({
    queryKey: ['playlist', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('destination_playlists')
        .select(`
          *,
          created_by_user:created_by_user_id(
            id,
            username,
            avatar_url
          )
        `)
        .eq('id', id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    if (playlist && !name) {
      const nameSlug = playlist.name.toLowerCase().replace(/\s+/g, '-');
      navigate(`/collections/${nameSlug}/${id}`, { replace: true });
    }
  }, [playlist, id, name, navigate]);

  const { data: places, isLoading: isPlacesLoading } = useQuery({
    queryKey: ['playlist-items', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('destination_playlist_items')
        .select(`
          *,
          created_by_user:created_by_user_id(
            id,
            username,
            avatar_url
          )
        `)
        .eq('playlist_id', id)
        .order('order_index', { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });

  // Create properly formatted collection object for ShareCollectionDialog
  const formattedCollection = React.useMemo(() => {
    console.log('Creating formattedCollection in Collection page:', { 
      playlist: !!playlist, 
      places: !!places, 
      placesLength: places?.length 
    });
    
    if (!playlist) {
      console.log('No playlist data for formattedCollection in Collection page');
      return null;
    }
    
    // Create place_items array with proper structure - always an array, never undefined
    const placeItems = Array.isArray(places) ? places.map(place => ({
      id: place.id,
      name: place.name || 'Unnamed Place',
      place_type: place.place_type || 'place',
      photos: place.photos || [],
      rating: place.rating || 0,
      address: place.address || ''
    })) : [];
    
    const formatted = {
      ...playlist,
      places_count: placeItems.length,
      place_items: placeItems
    };
    
    console.log('Collection page formatted collection:', {
      id: formatted.id,
      name: formatted.name,
      places_count: formatted.places_count,
      place_items_length: formatted.place_items?.length
    });
    
    return formatted;
  }, [playlist, places]);

  // Debug what's being passed to the dialog
  React.useEffect(() => {
    if (isShareDialogOpen) {
      console.log('Collection page: About to show ShareCollectionDialog with:', {
        formattedCollection: !!formattedCollection,
        collection_data: formattedCollection
      });
    }
  }, [isShareDialogOpen, formattedCollection]);

  const removeMutation = useMutation({
    mutationFn: async (placeId: string) => {
      if (!session?.user?.id) throw new Error('Must be logged in to remove places');
      
      const { error } = await supabase.rpc(
        'remove_place_from_collection',
        { 
          place_id: placeId,
          user_id: session.user.id
        }
      );

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playlist-items', id] });
      toast({
        title: "Success",
        description: "Place removed from collection",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const handlePlaceClick = (place: any) => {
    // Navigate to the place page instead of opening a modal
    navigate(`/places/${place.id}`);
  };

  const handleShareClick = () => {
    console.log('Share button clicked, formattedCollection:', formattedCollection);
    if (formattedCollection) {
      setIsShareDialogOpen(true);
    } else {
      console.error('Cannot open share dialog: formattedCollection is null');
      toast({
        title: "Error",
        description: "Unable to share collection at this time",
        variant: "destructive",
      });
    }
  };

  // Generate SEO metadata
  const getSeoDescription = () => {
    if (playlist?.description) {
      return playlist.description.length > 160 ? 
        `${playlist.description.substring(0, 157)}...` : 
        playlist.description;
    }
    return `Explore ${playlist?.name || 'this collection'} of travel destinations on Travilis. Browse through curated places and get travel inspiration.`;
  };

  const getSeoTitle = () => {
    return playlist?.name ? 
      `${playlist.name} Collection` : 
      'Travel Collection';
  };

  if (isPlaylistLoading || isPlacesLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-gray-200 rounded mb-4"></div>
          <div className="h-24 w-full bg-gray-200 rounded mb-8"></div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!playlist) {
    return (
      <div className="container mx-auto py-8">
        <SEO 
          title="Collection Not Found"
          description="The collection you're looking for couldn't be found."
          noIndex={true}
        />
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Collection not found</h2>
          <Button variant="ghost" onClick={() => navigate('/collections')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Collections
          </Button>
        </div>
      </div>
    );
  }

  const isOwner = session?.user?.id === playlist.user_id;

  // The canonical URL for SEO
  const collectionNameSlug = playlist.name.toLowerCase().replace(/\s+/g, '-');
  const canonicalUrl = `${window.location.origin}/collections/${collectionNameSlug}/${id}`;

  return (
    <div className="container mx-auto py-8">
      <SEO 
        title={getSeoTitle()}
        description={getSeoDescription()}
        image={places?.[0]?.image_url || '/og-image.png'}
        canonical={canonicalUrl}
        type="website"
      />
      
      <div className="mb-8">
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate('/collections')}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Collections
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">{playlist?.name}</h1>
            {playlist?.description && (
              <p className="text-muted-foreground mb-2">{playlist.description}</p>
            )}
            {playlist?.created_by_user && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={playlist.created_by_user.avatar_url || ''} />
                  <AvatarFallback><User className="w-4 h-4" /></AvatarFallback>
                </Avatar>
                <span>Created by {playlist.created_by_user.username || 'Anonymous'}</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {playlist?.is_public && (
              <Button 
                variant="outline"
                onClick={handleShareClick}
                className="flex items-center gap-2 bg-gradient-to-r from-primary/10 to-purple-500/10 border-primary/20 hover:from-primary/20 hover:to-purple-500/20"
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>
            )}
            {isOwner && (
              <Button 
                onClick={() => setIsAddPlaceDialogOpen(true)}
                className="bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
              >
                Add Place
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {places?.map((place) => (
          <PlaylistPlaceCard
            key={place.id}
            place={place}
            isOwner={isOwner}
            onRemove={(placeId) => removeMutation.mutate(placeId)}
            onClick={handlePlaceClick}
          />
        ))}
      </div>

      {session && isOwner && (
        <AddPlaceDialog
          isOpen={isAddPlaceDialogOpen}
          onClose={() => setIsAddPlaceDialogOpen(false)}
          playlistId={id!}
        />
      )}

      {formattedCollection && (
        <ShareCollectionDialog
          isOpen={isShareDialogOpen}
          onClose={() => setIsShareDialogOpen(false)}
          collection={formattedCollection}
        />
      )}
    </div>
  );
}
